<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { SVG } from '@svgdotjs/svg.js';
    import { remToPx } from '@oat-sa-private/ui-core';
    import { onMount, untrack } from 'svelte';
    import { minSizePx, checkmarkRadius } from './util/shapeDefaults.js';
    /**
     * Presentational component to render rectangle hotspot
     *
     * @property {number[]} coords array of hotspot's shape coordinates
     * @property {string} label text to be rendered in visual center of hotspot
     * @property {string} ariaLabel aria-label of hotspot for screen readers
     * @property {string} ariaLabelledBy id of the element that contains the label
     * @property {boolean} selected selected state of hotspot
     * @property {boolean} disabled disabled state of hotspot
     * @property {boolean} invisible invisible state of hotspot
     * @property {boolean} checkmark draw or not circle/label in the center of element
     * @property {number} scale scale shape
     */
    let {
        coords,
        label,
        ariaLabel,
        ariaLabelledBy,
        selected,
        disabled,
        invisible,
        checkmark = true,
        scale = 1,

        onmount,
        oncenter,
        onclick,
        onkeydown,
        onkeyup
    } = $props();

    let groupElement = $state();
    let svgGroup = $state();
    let cx = $state(null);
    let cy = $state(null);

    $effect(() => {
        if (svgGroup) {
            void (coords, checkmark, label, scale);
            untrack(() => {
                draw(svgGroup);
            });
        }
    });

    $effect(() => {
        if (cx !== null && cy !== null) {
            oncenter?.({ cx, cy });
        }
    });

    /**
     * Draw (or re-draw) the dynamic elements inside the <g>
     * @param {import('@svgdotjs/svg.js').Svg} groupEl
     */
    function draw(groupEl) {
        groupEl.clear();

        if (coords && coords.length) {
            cx = (coords[2] + coords[0]) / 2;
            cy = (coords[3] + coords[1]) / 2;
            let width = coords[2] - coords[0];
            let height = coords[3] - coords[1];

            const outerWidthOffset = remToPx(0.5);
            const innerWidthOffset = remToPx(1.25);

            // apply minSize if needed (to each dimension independently)
            // include offets into comparison to avoid negative values when rendered
            width = Math.max(minSizePx, width, outerWidthOffset, innerWidthOffset);
            height = Math.max(minSizePx, height, outerWidthOffset, innerWidthOffset);

            //outer white rect
            groupEl
                .rect(width - outerWidthOffset, height - outerWidthOffset)
                .cx(cx)
                .cy(cy)
                .addClass('shape-outer-border');
            //inner black rect
            groupEl
                .rect(width - innerWidthOffset, height - innerWidthOffset)
                .cx(cx)
                .cy(cy)
                .addClass('shape-inner-border');

            groupEl
                .rect(width - outerWidthOffset, height - outerWidthOffset)
                .cx(cx)
                .cy(cy)
                .addClass('shape-dashed-outline');

            if (checkmark) {
                if (label) {
                    groupEl.text(label).cx(cx).cy(cy).addClass('shape-checkmark');
                } else {
                    groupEl.circle(checkmarkRadius).attr({ cx, cy }).addClass('shape-checkmark');
                }
            }

            if (scale !== 1) {
                groupEl.each(function () {
                    this.scale(scale);
                });
            }
        }
    }

    onMount(() => {
        svgGroup = SVG(groupElement);
        onmount?.({ svgGroup });
    });
</script>

<g
    tabindex="-1"
    role="button"
    aria-label={ariaLabel}
    aria-labelledby={ariaLabelledBy}
    aria-disabled={disabled}
    bind:this={groupElement}
    class="shape"
    class:invisible
    class:selected
    {onclick}
    {onkeydown}
    {onkeyup} />
